import axios from "axios";

let _currentApiKey = "";

// Factory function para criar instância do axios com configuração dinâmica
const createDiscourseApi = (baseURL, apiKey, apiUsername) => {
  _currentApiKey = apiKey || "";
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      "User-Api-Key": _currentApiKey
    },
    withCredentials: false
  });
  instance.interceptors.request.use((config) => {
    const isPublicRequest =
      /^\/(categories|latest|top)\.json/.test(config.url) ||
      (config.method === 'get' && /^\/posts\.json/.test(config.url));

    if (isPublicRequest) {
      delete config.headers["User-Api-Key"];
    } else {
      config.headers["User-Api-Key"] = _currentApiKey || "";
    }
    return config;
  });
  return instance;
};

class DiscourseService {
  constructor() {
    this.apiInstance = null;
  }

  // Método para inicializar/atualizar a instância do axios
  initialize(baseURL, apiKey, apiUsername) {
    _currentApiKey = apiKey || "";
    this.apiInstance = createDiscourseApi(baseURL, apiKey, apiUsername);
  }

  // Atualiza só a key (evita recriar a instância a cada request)
  updateApiKey(apiKey) {
    _currentApiKey = apiKey || "";
  }

  // Garante que a instância está inicializada
  // NOTA: Este método não deve usar VITE_DISCOURSE_API_KEY (key master)
  // A inicialização deve sempre ser feita através do store que usa a API key do usuário
  ensureInitialized() {
    if (!this.apiInstance) {
      const baseURL = import.meta.env.VITE_DISCOURSE_API_URL || '';
      // Não usa key master - apenas string vazia para endpoints públicos (se houver)
      // A inicialização correta deve ser feita via store.initialize() que usa a API key do usuário
      const apiKey = '';
      const apiUsername = import.meta.env.VITE_DISCOURSE_API_USERNAME || 'system';
      this.initialize(baseURL, apiKey, apiUsername);
    }
  }

  // ========================================
  // CATEGORIES
  // ========================================
  async getCategories() {
    this.ensureInitialized();
    return this.apiInstance.get('/categories.json?include_subcategories=true');
  }

  async getCategory(id) {
    this.ensureInitialized();
    return this.apiInstance.get(`/c/${id}.json`);
  }

  async getCategoryTopics(categoryId, params = {}) {
    this.ensureInitialized();
    
    // Construir query params
    const queryParams = { ...params };
    if (queryParams.limit) {
      queryParams.per_page = queryParams.limit;
      delete queryParams.limit;
    }
    
    const queryString = new URLSearchParams(queryParams).toString();
    
    // Formato: /c/{slug}/{id}.json
    // Se slug foi fornecido nos params, usar; senão usar apenas ID (fallback)
    let url;
    if (params.categorySlug) {
      url = `/c/${params.categorySlug}/${categoryId}.json${queryString ? '?' + queryString : ''}`;
    } else {
      // Fallback: usar apenas ID se slug não estiver disponível
      url = `/c/${categoryId}.json${queryString ? '?' + queryString : ''}`;
    }
    
    return this.apiInstance.get(url);
  }

  // ========================================
  // EMOJIS
  // ========================================
  async getEmojis() {
    this.ensureInitialized();
    return this.apiInstance.get('/emojis.json');
  }

  // ========================================
  // TOPICS
  // ========================================
  async getTopics(params = {}) {
    this.ensureInitialized();
    const queryParams = new URLSearchParams(params).toString();
    return this.apiInstance.get(`/latest.json?${queryParams}`);
  }

  async getLatestTopics(params = {}) {
    this.ensureInitialized();
    const queryParams = new URLSearchParams(params).toString();
    return this.apiInstance.get(`/latest.json?${queryParams}`);
  }

  async getLatestPosts() {
    this.ensureInitialized();
    return this.apiInstance.get('/posts.json');
  }

  async getUnreadTopics(params = {}) {
    this.ensureInitialized();
    const queryParams = new URLSearchParams(params).toString();
    return this.apiInstance.get(`/unread.json?${queryParams}`);
  }

  async getTopTopics(period = 'monthly', categoryId = null) {
    this.ensureInitialized();
    const params = { period };
    if (categoryId) {
      params.category = categoryId;
    }
    const queryParams = new URLSearchParams(params).toString();
    return this.apiInstance.get(`/top.json?${queryParams}`);
  }

  async getBookmarks(username, params = {}) {
    this.ensureInitialized();
    
    // Converter limit para per_page se necessário
    const queryParams = { ...params };
    if (queryParams.limit) {
      queryParams.per_page = queryParams.limit;
      delete queryParams.limit;
    }
    
    const queryString = new URLSearchParams(queryParams).toString();
    return this.apiInstance.get(`/u/${username}/bookmarks.json${queryString ? '?' + queryString : ''}`);
  }

  async getTopic(id, params = {}) {
    this.ensureInitialized();

    this.apiInstance.get('/message-bus/poll', {
        headers: {
          'Discourse-Deferred-Track-View': 'true',
          'Discourse-Deferred-Track-View-Topic-Id': id.toString()
        }
      }).catch (e => {
      // Ignora erros do tracking
    });

    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `/t/${id}.json?${queryString}` : `/t/${id}.json`;
    return this.apiInstance.get(url, {
      headers: {
        'Discourse-Track-View': 'true',
        'Discourse-Track-View-Topic-Id': id.toString()
      }}
    );
  }

  async markTopicAsRead(topicId, timings = {}, topicTime = null) {
    this.ensureInitialized();
    const data = {
      topic_id: topicId,
      timings: timings
    };
    
    if (topicTime !== null) {
      data.topic_time = topicTime;
    }
    
    // Usar endpoint /t/:topic_id/timings
    return this.apiInstance.post(`/t/${topicId}/timings`, data);
  }

  async createTopic(topicData) {
    this.ensureInitialized();
    return this.apiInstance.post('/posts.json', {
      title: topicData.title,
      raw: topicData.content,
      category: topicData.categoryId
    });
  }

  // ========================================
  // POSTS
  // ========================================
  async getPost(id) {
    this.ensureInitialized();
    return this.apiInstance.get(`/posts/${id}.json`);
  }

  async createPost(postData) {
    this.ensureInitialized();
    return this.apiInstance.post('/posts.json', {
      topic_id: postData.topicId,
      raw: postData.content,
      reply_to_post_number: postData.replyToPostNumber
    });
  }

  async updatePost(postId, content) {
    this.ensureInitialized();
    return this.apiInstance.put(`/posts/${postId}.json`, {
      post: {
        raw: content
      }
    });
  }

  async deletePost(postId) {
    this.ensureInitialized();
    return this.apiInstance.delete(`/posts/${postId}.json`);
  }

  // ========================================
  // SEARCH
  // ========================================
  async search(query, params = {}) {
    this.ensureInitialized();
    const searchParams = new URLSearchParams({
      q: query,
      ...params
    }).toString();
    return this.apiInstance.get(`/search.json?${searchParams}`);
  }

  // ========================================
  // USER
  // ========================================
  async getCurrentUser() {
    this.ensureInitialized();
    return this.apiInstance.get('/session/current.json');
  }

  async getUser(username) {
    this.ensureInitialized();
    return this.apiInstance.get(`/u/${username}.json`);
  }

  async updateUserAvatar(username, uploadId) {
    this.ensureInitialized();
    
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    formData.append('type', 'uploaded');
    
    return this.apiInstance.put(`/u/${username}/preferences/avatar/pick.json`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // ========================================
  // UPLOADS
  // ========================================
  async uploadFile(file, type = 'image') {
    this.ensureInitialized();
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    return this.apiInstance.post('/uploads.json', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // ========================================
  // BOOKMARKS
  // ========================================
  async createBookmark(postId, reminderAt = null, name = null, autoDeletePreference = null) {
    this.ensureInitialized();
    const data = {
      bookmarkable_id: postId,
      bookmarkable_type: 'Post'
    };
    
    if (reminderAt) {
      data.reminder_at = reminderAt;
    }
    if (name) {
      data.name = name;
    }
    if (autoDeletePreference) {
      data.auto_delete_preference = autoDeletePreference;
    }
    
    return this.apiInstance.post('/bookmarks.json', data);
  }

  async updateBookmark(bookmarkId, name = null, reminderAt = null) {
    this.ensureInitialized();
    const data = {};
    
    if (name) {
      data.name = name;
    }
    if (reminderAt) {
      data.reminder_at = reminderAt;
    }
    
    return this.apiInstance.put(`/bookmarks/${bookmarkId}.json`, data);
  }

  async deleteBookmark(postId) {
    this.ensureInitialized();
    return this.apiInstance.delete(`/posts/${postId}/bookmark.json`);
  }

  async deleteBookmarkById(bookmarkId) {
    this.ensureInitialized();
    return this.apiInstance.delete(`/bookmarks/${bookmarkId}.json`);
  }

  // ========================================
  // LIKES
  // ========================================
  async createLike(postId) {
    this.ensureInitialized();
    return this.apiInstance.post('/post_actions.json', {
      id: postId,
      post_action_type_id: 2 // 2 = Like
    });
  }

  async deleteLike(postId) {
    this.ensureInitialized();
    return this.apiInstance.delete(`/post_actions/${postId}.json?post_action_type_id=2`, {
      data: {
        post_action_type_id: 2 // 2 = Like
      }
    });
  }

  async getPostActionUsers(postId, postActionTypeId = 2) {
    this.ensureInitialized();
    const params = new URLSearchParams({
      id: postId,
      post_action_type_id: postActionTypeId
    }).toString();
    return this.apiInstance.get(`/post_action_users.json?${params}`);
  }
}

export default new DiscourseService();
